import { useState, useRef } from "react";
import { useAccess } from "../../../../features/access/context/AccessProvider";
import { loadBusinessSend } from "../../models/shared/businessSendAttempt";
import { shareQuote, fetchQuoteSharePreflight } from "../../models/price-drawer/pricingModel";

export function useQuoteShareCommands({ actionSnippet, qrLoading, bindingError, userId, card, selectedPartsForInjection, messageText, getPayload, onApprovalCreated, parts, onSuggestUnavailableNotice, confirmUnavailable, onClose, receiveReview, notify }) {
  const { can } = useAccess();
  const [sending, setSending] = useState(false);
  const [manualShare, setManualShare] = useState(null);
  const [recordingManual, setRecordingManual] = useState(false);
  const sendFlightRef = useRef(false);
  const handleSend = async () => {
    if (!actionSnippet || qrLoading || bindingError) return;
    if (sendFlightRef.current) return;
    const savedSend = loadBusinessSend(userId, card?.id, "share-quote");
    if (savedSend && savedSend.state !== "completed") {
      notify.error(savedSend.error || "Recover the existing frozen quote request before creating another submission.");
      return;
    }
    if (!can("sales_pipeline.share_customer_quote")) return;
    if (selectedPartsForInjection.length === 0) {
      notify.warning("Select at least one part to include in the quote before sending.");
      return;
    }
    if (!messageText.trim()) return;

    const payload = getPayload();

    sendFlightRef.current = true;
    setSending(true);
    try {
      const preflight = await fetchQuoteSharePreflight(card?.id);
      if (!preflight?.success) throw new Error(preflight?.message || "Could not verify quote sharing availability");
      const decision = preflight.data;
      if (typeof decision?.approval_required !== "boolean") throw new Error("Quote preflight did not return an approval decision. Please retry.");
      if (!decision?.approval_required && decision?.communication_channel?.key !== "wati") {
        setManualShare({ payload, channel: decision.communication_channel });
        return;
      }

      const res = decision.approval_required ? await shareQuote(card?.id, payload)
        : await shareQuote(card?.id, payload, { directWati: true, actor: userId });
      if (!res.success) throw new Error(res.message);
      if (res.mode === "approval_required" || res.data?.approval_required) {
        notify.info("Quote submitted for admin approval");
        onApprovalCreated?.();
      } else {
        notify.success(res.messaging_operation ? "Quote accepted by the provider and recorded; delivery is tracked in chat." : res.message || "Quote shared with customer");
        // The customer just heard good news about some parts — nudge staff to
        // also tell them about any part nobody ever quoted, in the same beat.
        const zeroQuoteParts = parts.filter(p => (p.supplier_quotes ?? p.quotes ?? []).length === 0);
        if (zeroQuoteParts.length > 0 && onSuggestUnavailableNotice) {
          confirmUnavailable(zeroQuoteParts);
        }
      }
      onClose();
    } catch (e) {
      if (!receiveReview(e)) notify.error(e?.response?.data?.message || e?.message || "Failed to submit quote");
    } finally {
      sendFlightRef.current = false;
      setSending(false);
    }
  };

  const handleManualShare = async () => {
    if (!can("sales_pipeline.share_customer_quote")) return;
    if (!manualShare?.payload) return;
    setRecordingManual(true);
    try {
      const res = await shareQuote(card?.id, manualShare.payload);
      if (!res.success) throw new Error(res.message);
      if (res.mode === "approval_required" || res.data?.approval_required) {
        notify.info("Quote submitted for admin approval");
        onApprovalCreated?.();
      } else {
        notify.success(res.message || "Quote sharing recorded");
      }
      setManualShare(null);
      onClose();
    } catch (e) {
      if (!receiveReview(e)) notify.error(e?.response?.data?.message || e?.message || "Failed to record quote");
    } finally {
      setRecordingManual(false);
    }
  };


  return { sending, setSending, manualShare, setManualShare, recordingManual, handleSend, handleManualShare };
}
